import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Home, ClipboardList, IndianRupee, Wallet, AlertCircle, Users, Target, AlertTriangle, PhoneCall, MapPinned } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { KpiCard, PageHeader, Spinner, StatusBadge } from "../../components/common";
import { StaffAvatar, StaffName, StaffLabel, staffLabelText } from "../../components/StaffAvatar";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, fmtINR, fmtDate, getAuth } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { LiveBadge } from "../../components/LiveBadge";
import { MyTargetsCards } from "../../components/MyTargetsCards";

const COLORS = ["hsl(206,88%,28%)", "hsl(152,55%,32%)", "hsl(38,92%,45%)", "hsl(199,89%,40%)", "hsl(0,72%,50%)", "hsl(260,50%,50%)", "hsl(180,50%,35%)"];

/** Phase 112: tells the supervisor exactly which zones the numbers below cover.
 *  Phase 166: a zone the supervisor holds only partly reads "Zone-1 · 4 of 6 wards". */
const ScopeBanner = ({ scope, role }) => {
  if (!scope) return null;
  if (scope.scoped) {
    const cov = Object.fromEntries((scope.zone_coverage || []).map((c) => [c.zone, c]));
    const label = (z) => {
      const c = cov[z];
      return c && (c.mode === "wards" || c.carved) ? `${z} · ${c.wards_seen} of ${c.wards_total} wards` : z;
    };
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-accent/40 px-3 py-2 mb-4 text-sm" data-testid="dashboard-scope-banner">
        <MapPinned className="h-4 w-4 text-primary shrink-0" />
        <span className="text-muted-foreground">Showing data for your {scope.split ? "wards" : `zone${scope.zones.length === 1 ? "" : "s"}`} only:</span>
        {scope.zones.map((z) => (
          <Badge key={z} variant="secondary" className="font-medium" data-testid={`dashboard-scope-zone-${z}`}>{label(z)}</Badge>
        ))}
        <span className="text-xs text-muted-foreground ml-auto tabular-nums" data-testid="dashboard-scope-count">
          {scope.properties_in_scope?.toLocaleString("en-IN")} properties in scope
        </span>
      </div>
    );
  }
  if (role === "supervisor") {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 mb-4 text-sm" data-testid="dashboard-scope-banner-unmapped">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        No zone is mapped to your account yet, so figures cover the entire municipality. Ask the admin to map your zones in Users {"&"} Staff.
      </div>
    );
  }
  return null;
};

export default function SupervisorDashboard() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [collectors, setCollectors] = useState([]);
  const [wardProgress, setWardProgress] = useState(null);
  const [zeroActivity, setZeroActivity] = useState(null);
  const role = getAuth()?.user?.role || null;

  const live = useLiveCollections(code);   // Phase 149: KPIs, trend and ward progress follow the live ledger
  useEffect(() => {
    api.get(`/t/${code}/dashboard/supervisor`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/t/${code}/supervisor/ward-progress`).then((r) => setWardProgress(r.data)).catch(() => setWardProgress([]));
  }, [code, live]);
  useEffect(() => {
    api.get(`/t/${code}/collectors`).then((r) => setCollectors(r.data)).catch(() => {});
    api.get(`/t/${code}/field/zero-activity`).then((r) => setZeroActivity(r.data)).catch(() => setZeroActivity(null));
  }, [code]);

  if (!data) return <Spinner />;
  const pct = data.total_demand > 0 ? Math.round((data.collected / data.total_demand) * 100) : 0;
  const modes = data.payment_modes || [];
  const modeTotal = modes.reduce((a, m) => a + (m.amount || 0), 0);
  const trend = data.collection_trend || [];
  const scopeLabel = data.scope?.scoped ? data.scope.zones.join(", ") : "entire municipality";

  return (
    <div>
      <PageHeader title="Supervisor Dashboard"
        subtitle={data.scope?.scoped ? `Assignment and collection monitoring • ${scopeLabel}` : "Assignment and collection monitoring"}>
        <LiveBadge />
        <Button asChild data-testid="goto-assignments-button">
          <Link to={`/${code}/supervisor/assignments`}>Assign Properties</Link>
        </Button>
      </PageHeader>

      <ScopeBanner scope={data.scope} role={role} />
      {zeroActivity && zeroActivity.collectors.length > 0 && (
        <Card className={`shadow-sm mb-4 border-l-4 ${zeroActivity.is_evening ? "border-l-destructive" : "border-l-amber-500"}`}
          data-testid="zero-activity-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                zeroActivity.is_evening ? "bg-destructive/10 text-destructive" : "bg-amber-500/10 text-amber-600"}`}>
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold" data-testid="zero-activity-title">
                  {zeroActivity.is_evening
                    ? `Evening check — ${zeroActivity.collectors.length} of ${zeroActivity.total_collectors} collectors have no field activity today`
                    : `${zeroActivity.collectors.length} of ${zeroActivity.total_collectors} collectors have no field activity yet today`}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  No visits, collections, surveys or reassessments logged today ({fmtDate(zeroActivity.date)}).
                  {zeroActivity.is_evening ? " Follow up before end of day." : ""}
                </p>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  {zeroActivity.collectors.map((c) => (
                    <span key={c.id}
                      className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs"
                      data-testid={`zero-activity-chip-${c.name}`}>
                      <StaffAvatar code={code} userId={c.id} name={c.name} size="xs" className="-ml-1" />
                      <span className="font-medium"><StaffLabel code={code} userId={c.id} name={c.name} empCode={c.employee_id} /></span>
                      <span className="text-muted-foreground">
                        {c.last_activity_at ? `last active ${fmtDate(c.last_activity_at)}` : "never active"}
                      </span>
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="text-primary hover:opacity-80"
                          title={`Call ${staffLabelText(c.name, c.employee_id)}`} data-testid={`zero-activity-call-${c.name}`}>
                          <PhoneCall className="h-3 w-3" />
                        </a>
                      )}
                    </span>
                  ))}
                </div>
              </div>
              <Button asChild variant="outline" size="sm" className="shrink-0 h-8 text-xs" data-testid="zero-activity-report-link">
                <Link to={`/${code}/supervisor/field-activity`}>Field activity</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {data.my_targets && (
        <div className="mb-6 space-y-1.5" data-testid="dashboard-my-targets">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium flex items-center gap-1.5"><Target className="h-4 w-4 text-primary" /> My targets this month</p>
            <Button asChild variant="ghost" size="sm" className="h-7 text-xs" data-testid="dashboard-my-targets-scorecard-link">
              <Link to={`/${code}/supervisor/scorecard`}>Open scorecard</Link>
            </Button>
          </div>
          <MyTargetsCards mt={data.my_targets} />
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4 mb-6" data-testid="supervisor-kpi-grid">
        <KpiCard label="Total Properties" value={data.total_properties} icon={Home} testId="kpi-total-properties" />
        <KpiCard label="Total Demand" value={data.total_demand} money icon={IndianRupee} testId="kpi-total-demand" />
        <KpiCard label="Collected" value={data.collected} money sub={`${pct}% realized · ${(data.receipts || 0).toLocaleString("en-IN")} receipts (all sources)`} icon={Wallet} testId="kpi-collected" />
        <KpiCard label="Balance Due" value={data.balance} money icon={AlertCircle} testId="kpi-balance" />
        <KpiCard label="Assigned" value={data.assigned} sub={`${data.unassigned} unassigned`} icon={ClipboardList} testId="kpi-assigned" />
      </div>

      {/* Phase 112: zone-scoped collection trend + payment mode split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card className="shadow-sm" data-testid="supervisor-collection-trend-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">Collection trend</CardTitle>
            <p className="text-xs text-muted-foreground">Last {trend.length} collection days • {scopeLabel}</p>
          </CardHeader>
          <CardContent className="h-64">
            {trend.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center pt-20" data-testid="supervisor-trend-empty">No payments recorded yet</p>
            ) : (
              <div className="h-full" data-testid="supervisor-collection-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmtINR(v)} />
                    <Area type="monotone" dataKey="amount" name="Collected" stroke="hsl(206,88%,28%)" fill="hsl(206,88%,28%)" fillOpacity={0.15} activeDot={{ r: 5 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="shadow-sm" data-testid="supervisor-payment-modes-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">Payment mode split</CardTitle>
            <p className="text-xs text-muted-foreground">{fmtINR(modeTotal)} across {modes.reduce((a, m) => a + (m.count || 0), 0).toLocaleString("en-IN")} receipts • {scopeLabel}</p>
          </CardHeader>
          <CardContent className="sm:h-64">
            {modes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center pt-20" data-testid="supervisor-modes-empty">No payments recorded yet</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 h-full">
                <div className="h-56 sm:h-full" data-testid="supervisor-payment-modes-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={modes} dataKey="amount" nameKey="mode" innerRadius={50} outerRadius={80} paddingAngle={2}>
                        {modes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtINR(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="flex flex-col justify-center gap-1 overflow-y-auto" data-testid="supervisor-payment-modes-list" aria-label="Payment modes">
                  {modes.map((m, i) => (
                    <li key={m.mode} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm" data-testid={`supervisor-mode-${m.mode}`}>
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} aria-hidden="true" />
                      <span className="font-medium truncate">{m.mode}</span>
                      <span className="ml-auto tabular-nums text-muted-foreground text-xs">{m.count} rcpt</span>
                      <span className="tabular-nums font-medium w-24 text-right">{fmtINR(m.amount)}</span>
                      <span className="tabular-nums text-muted-foreground text-xs w-10 text-right">{modeTotal > 0 ? `${Math.round((m.amount / modeTotal) * 100)}%` : "—"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Assignment status</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.assignment_status || {}).map(([s, count]) => (
                <div key={s} className="flex items-center justify-between border rounded-lg px-3 py-2">
                  <StatusBadge status={s} />
                  <p className="font-semibold tabular-nums">{count}</p>
                </div>
              ))}
              {Object.keys(data.assignment_status || {}).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No assignments yet</p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><Users className="h-4 w-4" /> Collector performance</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table data-testid="collector-performance-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Collector</TableHead>
                  <TableHead className="text-right">Assigned</TableHead>
                  <TableHead className="text-right">Collected</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collectors.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium text-sm"><StaffName code={code} userId={c.id} name={c.name} empCode={c.employee_id} size="sm" /></TableCell>
                    <TableCell className="text-right tabular-nums">{c.stats.assigned}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.stats.collected}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{fmtINR(c.stats.amount_collected)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {wardProgress && wardProgress.length > 0 && (
        <Card className="shadow-sm mt-4" data-testid="supervisor-ward-progress-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" /> Ward collection progress
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Due vs collected across your collectors' wards — slowest (highest outstanding) first
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="supervisor-ward-progress-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Ward</TableHead>
                  <TableHead className="hidden lg:table-cell">Collectors</TableHead>
                  <TableHead className="text-right">Properties</TableHead>
                  <TableHead className="w-[220px]">Progress</TableHead>
                  <TableHead className="text-right">Collected</TableHead>
                  <TableHead className="text-right">Due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wardProgress.map((w) => (
                  <TableRow key={w.ward_id || "none"} data-testid={`supervisor-ward-row-${w.ward_name}`}>
                    <TableCell className="font-medium text-sm">
                      {w.ward_name}
                      {w.zone_name && <p className="text-xs font-normal text-muted-foreground">{w.zone_name}</p>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(w.collectors || []).slice(0, 3).map((n) => (
                          <Badge key={n} variant="outline" className="text-[10px] font-normal">{n}</Badge>
                        ))}
                        {(w.collectors || []).length > 3 && (
                          <Badge variant="secondary" className="text-[10px] font-normal">+{w.collectors.length - 3} more</Badge>
                        )}
                        {(w.collectors || []).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{w.properties}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={w.pct} className="h-2 flex-1" />
                        <span className="text-xs font-semibold tabular-nums w-11 text-right">{w.pct}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {fmtINR(w.paid)}
                      <p className="text-[11px] text-muted-foreground">of {fmtINR(w.total)}</p>
                    </TableCell>
                    <TableCell className={`text-right tabular-nums text-sm font-semibold ${w.balance > 0 ? "text-destructive" : "text-emerald-600"}`}>
                      {w.balance > 0 ? fmtINR(w.balance) : "Cleared"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
